import { inject, Injectable } from '@angular/core';
import { DialogHelper } from '@common/modules/dialog/dialog.helper';
import { ActionDisabledCondition, ActionItemOptions, PersonalSelectionService, UserAction } from '@mediahaven/shared';
import { ApiFragment, Mh2Entity, NavigationLink } from '@stylehaven/components';
import { Observable, of } from 'rxjs';
import { mapTo, switchMap, take, tap } from 'rxjs/operators';
import { MediaHavenActionType } from '../../../entities/action-types.entity';
import { ActionService } from '../../abstract/action.service.interface';

@Injectable({ providedIn: 'root' })
export class EmptySelectionActionService extends ActionService {
    protected readonly action: MediaHavenActionType = MediaHavenActionType.EMPTY_SELECTION;

    // Services
    private readonly selectionService: PersonalSelectionService = inject(PersonalSelectionService);
    private readonly dialogHelper: DialogHelper = inject(DialogHelper);

    // Behaviour
    protected readonly ENABLE_BUSY_TOAST: boolean = false;
    protected readonly REFRESH_ON_ACTION: boolean = true;
    protected readonly DISABLE_ON_EXECUTE: boolean = true;

    /* Empty Selection
     * Available when:
     *  * Record is of RecordType Selection
     *  * Selection contains more than 0 records
     *  * Not in share context
     *
     * Disabled when:
     *  * Never
     */
    public getUserAction(record: ApiFragment, actionItemOptions: ActionItemOptions): Observable<UserAction> {
        return of(this.userActionBuilder(record, actionItemOptions));
    }

    public userActionBuilder(record: ApiFragment, actionItemOptions: ActionItemOptions): UserAction {
        const isSelection: boolean = record.isPersonalSelection();
        const inShareContext: boolean = !!actionItemOptions.shareContext;
        const hasItemsInSelections: boolean = record.getSelectionItemsIds().length > 0;

        const availableConditions: boolean[] = [isSelection, hasItemsInSelections, !inShareContext];
        const disableConditions: ActionDisabledCondition[] = [];

        return new UserAction(availableConditions, disableConditions);
    }

    protected onAction(record: ApiFragment, actionItemOption: ActionItemOptions): Observable<boolean> {
        const message: string = this.translate('items.delete-confirm-dialog.from-selection.empty');
        const title: string = this.translate('actions.selection.empty');
        const yes: string = this.translate('actions.selection.empty');
        const no: string = this.translate('actions.common.cancel');

        return this.dialogHelper.confirm({ title, message, yes, no }).pipe(
            tap(() => this.initBusyToast(record, actionItemOption, true)),
            switchMap(() => this.selectionService.emptySelection(record)),
            mapTo(true),
            take(1)
        );
    }

    public getLabelKey(target: ApiFragment): string {
        return 'actions.selection.empty';
    }

    public getToastLabelKey(target: ApiFragment): string {
        return 'action-toasts.empty-selection';
    }

    public getToastEntity(target: ApiFragment, actionItemOptions: ActionItemOptions): Mh2Entity | string {
        return 'all';
    }

    public getSuccessToastLink(): NavigationLink | undefined {
        return undefined;
    }

    public getSuccessToastLinkLabelKey(target: ApiFragment, actionItemOptions: ActionItemOptions): string {
        return '';
    }

    public getToastTranslationOptions(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): Record<string, string> {
        return {};
    }
}
